/**
 * apiErrors.js — Normalização de erros de API em PT-BR
 * 
 * Uso:
 *   const { data, error } = await parseApiResponse(response);
 *   if (error) toast.error(error.message);
 * 
 *   // Ou em catch:
 *   catch (e) { toast.error(normalizeAppError(e).message); }
 */

const HTTP_MESSAGES = {
  400: 'Requisicao invalida',
  401: 'Email ou senha invalidos',
  403: 'Voce nao tem permissao para essa acao',
  404: 'Recurso nao encontrado',
  409: 'Conflito de dados',
  422: 'Dados invalidos, revise os campos',
  429: 'Muitas tentativas, aguarde e tente novamente',
  500: 'Erro interno do servidor',
  502: 'Servidor indisponivel no momento',
  503: 'Servidor em manutencao',
  504: 'Servidor demorou para responder',
};

const CONTEXT_MESSAGES = {
  login: { 401: 'Email ou senha invalidos', 403: 'Conta bloqueada ou sem permissao' },
  register: { 409: 'Este email ja esta cadastrado', 422: 'Dados invalidos, revise os campos' },
  recover: { 404: 'Email nao encontrado no sistema' },
};

/**
 * Lê o body de um Response UMA ÚNICA VEZ e retorna { data, error }.
 * Nunca faz double-read. Nunca expõe erro bruto.
 */
export async function parseApiResponse(response, context) {
  let data = null;
  let rawText = '';

  try {
    rawText = await response.text();
    if (rawText) {
      data = JSON.parse(rawText);
    }
  } catch (_) {
    // Body não é JSON — pode ser HTML do nginx
    data = null;
  }

  if (response.ok) {
    return { data: data || {}, error: null };
  }

  // Extrair mensagem do backend (detail ou message)
  const backendMsg = data?.detail || data?.message || '';

  // Buscar mensagem contextual
  const contextMap = context ? CONTEXT_MESSAGES[context] : null;
  const contextMsg = contextMap?.[response.status];

  // Montar mensagem user-friendly
  const userMessage = contextMsg
    || (backendMsg && !looksLikeRawError(backendMsg) ? backendMsg : null)
    || HTTP_MESSAGES[response.status]
    || `Erro do servidor (${response.status})`;

  const error = {
    title: getTitleForStatus(response.status),
    message: userMessage,
    code: codeFromStatus(response.status),
    status: response.status,
    rawMessage: backendMsg || rawText?.substring(0, 200),
    userFriendly: true,
  };

  // Log técnico apenas no console
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[API Error] ${response.status} ${response.url}`, error.rawMessage);
  }

  return { data, error };
}

/**
 * Normaliza qualquer erro JS/fetch para mensagem PT-BR.
 * Usar em blocos catch.
 */
export function normalizeAppError(err, context) {
  if (!err) return { title: 'Erro', message: 'Erro desconhecido', code: 'UNKNOWN', status: 0, userFriendly: true };

  const msg = err?.message || String(err);

  // Network / fetch errors
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('ERR_NETWORK')) {
    return { title: 'Sem conexao', message: 'Sem conexao com o servidor', code: 'NETWORK_ERROR', status: 0, userFriendly: true };
  }
  if (msg.includes('body stream already read') || msg.includes('Body has already been consumed')) {
    return { title: 'Erro de resposta', message: 'Resposta invalida do sistema', code: 'CLIENT_PARSE_ERROR', status: 0, userFriendly: true };
  }
  if (msg.includes('AbortError') || msg.includes('aborted')) {
    return { title: 'Cancelado', message: 'Requisicao cancelada', code: 'ABORTED', status: 0, userFriendly: true };
  }
  if (msg.includes('timeout') || msg.includes('Timeout')) {
    return { title: 'Timeout', message: 'Servidor demorou para responder', code: 'TIMEOUT', status: 0, userFriendly: true };
  }
  if (msg.includes('JSON') || msg.includes('Unexpected token')) {
    return { title: 'Erro de resposta', message: 'Resposta invalida do sistema', code: 'PARSE_ERROR', status: 0, userFriendly: true };
  }

  // Context-specific
  const contextMap = context ? CONTEXT_MESSAGES[context] : null;
  if (contextMap) {
    // Check if message contains known backend messages
    if (msg.includes('Invalid') || msg.includes('invalid')) {
      return { title: 'Falha', message: contextMap[401] || 'Credenciais invalidas', code: 'AUTH_INVALID', status: 401, userFriendly: true };
    }
  }

  // If message looks technical, hide it
  if (looksLikeRawError(msg)) {
    return { title: 'Erro', message: 'Ocorreu um erro inesperado', code: 'UNEXPECTED', status: 0, rawMessage: msg, userFriendly: true };
  }

  // Backend message that looks user-safe
  return { title: 'Erro', message: msg, code: 'APP_ERROR', status: 0, userFriendly: true };
}

// --- Helpers internos ---

function looksLikeRawError(msg) {
  if (!msg || typeof msg !== 'string') return false;
  const rawPatterns = [
    'TypeError', 'ReferenceError', 'SyntaxError',
    'Cannot read prop', 'undefined is not', 'null is not',
    'Failed to execute', 'body stream', 'Unexpected token',
    'NetworkError', 'ERR_', 'ECONNREFUSED',
    'at Object.', 'at Function.', 'at async',
    '<html', '<!DOCTYPE', 'Internal Server Error',
  ];
  return rawPatterns.some(p => msg.includes(p));
}

function getTitleForStatus(status) {
  if (status === 401) return 'Nao autenticado';
  if (status === 403) return 'Sem permissao';
  if (status === 404) return 'Nao encontrado';
  if (status === 422) return 'Dados invalidos';
  if (status === 429) return 'Limite atingido';
  if (status >= 500) return 'Erro do servidor';
  return 'Erro';
}

function codeFromStatus(status) {
  if (status === 401) return 'AUTH_UNAUTHORIZED';
  if (status === 403) return 'AUTH_FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 422) return 'VALIDATION_ERROR';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 500) return 'SERVER_ERROR';
  return `HTTP_${status}`;
}
